import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getWorkflowForEditing } from '@/lib/api/generated/api';

import type { DraftSource } from '../builder/draftReducer';
import { workflowKeys } from '../queryKeys';
import { useWorkflow } from './useWorkflow';

/**
 * What the builder opens on (spec 144 FR-005).
 *
 * A workflow never published is its own draft, and comes from the workspace
 * aggregate as it always has. A published one is read through its
 * pending-changes door, which answers with the workflow and, when someone has
 * saved a draft of it, that draft — the board keeps reading the aggregate, so
 * it never sees one.
 *
 * Read once per visit: no refetch on focus, and dropped from the cache once the
 * builder closes. A background read that brought a colleague's newer draft
 * would have nowhere honest to go while the author has edits on screen; the
 * save that follows is refused as stale instead, and Reload is the way to it.
 */
export function useWorkflowForEditing(
  workspaceId: string,
  workflowKey: string,
) {
  const aggregate = useWorkflow(workspaceId, workflowKey);
  const workflow = aggregate.workflow;
  const published = workflow?.status === 'published';
  const workflowId = workflow?.id ?? '';

  const editing = useQuery({
    queryKey: workflowKeys.editing(workspaceId, workflowId),
    queryFn: async ({ signal }) => {
      const response = await getWorkflowForEditing(workspaceId, workflowId, {
        signal,
      });
      if (response.status === 200) return response.data;
      // Deleted between the list read and this one: the route's not-found.
      if (response.status === 404) return null;
      throw new Error('This workflow could not be loaded.');
    },
    enabled: published,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  const { refetch: refetchAggregate } = aggregate;
  const { refetch: refetchEditing } = editing;
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchAggregate(),
      published ? refetchEditing() : undefined,
    ]);
  }, [refetchAggregate, refetchEditing, published]);

  const source: DraftSource | undefined = !workflow
    ? undefined
    : published
      ? (editing.data ?? undefined)
      : workflow;

  return {
    source,
    isPending: aggregate.isPending || (published && editing.isPending),
    loadFailed: aggregate.loadFailed || (published && editing.isError),
    refetch,
  };
}
